import React from "react";
import { motion } from "framer-motion";
import { Check, Home, Building2, MapPin, ShieldCheck } from "lucide-react";

export default function Services() {
  const tiers = [
    {
      icon: <Home className="h-5 w-5" />,
      tag: "Residential",
      price: "From $99",
      title: "Homes we love",
      blurb:
        "Hand-washed glass, scrubbed tracks, streak-free finish. Single-story and multi-story homes welcome.",
      list: [
        "Interior and exterior options",
        "Tracks scrubbed clean — always included",
        "Squeegee finish on every pane",
      ],
      testid: "service-card-residential",
    },
    {
      icon: <Building2 className="h-5 w-5" />,
      tag: "Commercial",
      price: "Custom quote",
      title: "Storefronts & offices",
      blurb:
        "Fully insured commercial service — storefronts, offices, restaurants, and multi-tenant properties. Flexible scheduling before or after hours.",
      list: [
        "Weekly, monthly, or one-time visits",
        "Insured for commercial work",
        "Call or text for a tailored quote",
      ],
      testid: "service-card-commercial",
    },
    {
      icon: <MapPin className="h-5 w-5" />,
      tag: "Service area",
      price: "San Angelo +",
      title: "West Texas coverage",
      blurb:
        "Free travel inside San Angelo. Outside — a simple $3 / mile round-trip fee, shown up front in your quote.",
      list: [
        "Wall, Miles, Christoval, Mertzon",
        "Ballinger, Eden, Sonora, Big Lake",
        "Brady, Abilene, Midland, Big Spring",
      ],
      testid: "service-card-area",
    },
  ];

  return (
    <section
      id="services"
      className="bg-white py-24 md:py-32 border-t border-black/5"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1A365D] mb-4">
              Services &amp; Pricing
            </div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-[#111827] leading-tight">
              Honest rates.
              <br />
              <span className="italic text-[#1A365D]">
                Remarkable results.
              </span>
            </h2>
          </div>
          <p className="max-w-md text-[#4B5563] font-light leading-relaxed">
            Every residential visit starts at $99 and always includes tracks.
            Commercial jobs are quoted to fit your space and schedule.
          </p>
        </div>

        {/* Trust strip */}
        <div className="mb-10 flex flex-wrap items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F1F2] text-[#1A365D] px-4 py-2 border border-[#1A365D]/15">
            <ShieldCheck className="h-4 w-4" />
            Fully insured LLC
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F1F2] text-[#1A365D] px-4 py-2 border border-[#1A365D]/15">
            <Home className="h-4 w-4" />
            Residential
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F1F2] text-[#1A365D] px-4 py-2 border border-[#1A365D]/15">
            <Building2 className="h-4 w-4" />
            Commercial
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#E8F1F2] text-[#1A365D] px-4 py-2 border border-[#1A365D]/15">
            <MapPin className="h-4 w-4" />
            San Angelo &amp; West Texas
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((t, i) => (
            <motion.div
              key={t.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group rounded-3xl border border-black/5 bg-[#F9F9F6] p-8 hover:bg-white transition-colors shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_20px_40px_rgba(0,0,0,0.06)]"
              data-testid={t.testid}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F1F2] text-[#1A365D]">
                  {t.icon}
                </span>
                <span className="text-xs uppercase tracking-[0.2em] text-[#4B5563]">
                  {t.tag}
                </span>
              </div>
              <div className="font-serif text-4xl md:text-5xl text-[#1A365D] leading-none">
                {t.price}
              </div>
              <h3 className="mt-4 font-serif text-2xl text-[#111827]">
                {t.title}
              </h3>
              <p className="mt-3 text-[#4B5563] font-light leading-relaxed">
                {t.blurb}
              </p>
              <ul className="mt-6 space-y-3">
                {t.list.map((li) => (
                  <li
                    key={li}
                    className="flex items-start gap-3 text-sm text-[#111827]"
                  >
                    <Check className="h-4 w-4 text-[#1A365D] shrink-0 mt-0.5" />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
